import { useEffect } from "react";
import { Link } from "react-router-dom";
import "./Messaging.css";
import { 
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Mail,
  MessageSquare,
  Smartphone,
  Bell,
  Zap,
  Target,
  TrendingUp,
  Users,
  Clock,
  BarChart3,
  Sparkles,
  Brain,
  Workflow,
  CheckCircle2,
  Send,
  MousePointer,
  Eye,
  Activity,
  PieChart,
  Settings,
  Layers,
  Cpu
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";

// ============================================
// MESSAGING PAGE VISUALIZATIONS
// ============================================

// Hero Visualization - Email Performance Dashboard
const MessagingHeroVisualization = () => {
  const channels = [
    { icon: Mail, label: 'Email', rate: '34.2%', color: '#3b82f6' },
    { icon: MessageSquare, label: 'SMS', rate: '28.7%', color: '#10b981' },
    { icon: Smartphone, label: 'WhatsApp', rate: '42.1%', color: '#25D366' },
  ];

  return (
    <div className="msg-hero-viz">
      <div className="msg-viz-header">
        <span className="msg-viz-label">ENGAGEMENT DASHBOARD</span>
        <span className="msg-viz-badge">LIVE</span>
      </div>
      
      <div className="msg-channels-grid">
        {channels.map((ch, i) => (
          <div key={i} className="msg-channel-card">
            <div className="channel-icon-box" style={{ backgroundColor: `${ch.color}15`, borderColor: ch.color }}>
              <ch.icon size={18} style={{ color: ch.color }} />
            </div>
            <div className="channel-data">
              <span className="channel-rate" style={{ color: ch.color }}>{ch.rate}</span>
              <span className="channel-label">{ch.label}</span>
            </div>
          </div>
        ))}
      </div>
      
      <div className="msg-performance-chart">
        <div className="chart-header">
          <span>Weekly Performance</span>
          <span className="chart-trend positive">+12.4%</span>
        </div>
        <svg viewBox="0 0 200 60" className="perf-chart-svg">
          <defs>
            <linearGradient id="chartGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3"/>
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
            </linearGradient>
          </defs>
          <path d="M0,50 L25,45 L50,35 L75,40 L100,25 L125,30 L150,15 L175,20 L200,10" 
                fill="none" stroke="#3b82f6" strokeWidth="2"/>
          <path d="M0,50 L25,45 L50,35 L75,40 L100,25 L125,30 L150,15 L175,20 L200,10 L200,60 L0,60 Z" 
                fill="url(#chartGrad)"/>
          {[0, 25, 50, 75, 100, 125, 150, 175, 200].map((x, i) => (
            <circle key={i} cx={x} cy={[50,45,35,40,25,30,15,20,10][i]} r="3" fill="#3b82f6"/>
          ))}
        </svg>
      </div>
      
      <div className="msg-quick-stats">
        <div className="quick-stat">
          <Send size={14} />
          <span>2.4M sent</span>
        </div>
        <div className="quick-stat">
          <Eye size={14} />
          <span>847K opens</span>
        </div>
        <div className="quick-stat">
          <MousePointer size={14} />
          <span>124K clicks</span>
        </div>
      </div>
    </div>
  );
};

// Personalization Engine Visualization
const PersonalizationVisualization = () => {
  const segments = [
    { name: 'High Intent', count: '124K', pct: 85 },
    { name: 'Re-engagement', count: '89K', pct: 72 },
    { name: 'New Subscribers', count: '45K', pct: 58 },
    { name: 'VIP Customers', count: '23K', pct: 94 },
  ];

  return (
    <div className="msg-viz-container personalization-viz">
      <div className="msg-viz-header">
        <span className="msg-viz-label">AUDIENCE SEGMENTS</span>
        <span className="msg-viz-count">4 ACTIVE</span>
      </div>
      
      <div className="segments-list">
        {segments.map((seg, i) => (
          <div key={i} className="segment-row">
            <div className="segment-info">
              <span className="segment-name">{seg.name}</span>
              <span className="segment-count">{seg.count} users</span>
            </div>
            <div className="segment-bar-container">
              <div className="segment-bar">
                <div className="segment-fill" style={{ width: `${seg.pct}%` }}></div>
              </div>
              <span className="segment-pct">{seg.pct}%</span>
            </div>
          </div>
        ))}
      </div>
      
      <div className="personalization-features">
        <div className="pers-feature">
          <Target size={14} />
          <span>Dynamic Content</span>
        </div>
        <div className="pers-feature">
          <Clock size={14} />
          <span>Send Time Optimization</span>
        </div>
        <div className="pers-feature">
          <Users size={14} />
          <span>1:1 Personalization</span>
        </div>
      </div>
    </div>
  );
};

// AI Content Generation Visualization
const AIContentVisualization = () => {
  const suggestions = [
    { type: 'Subject Line', text: '"Don\'t miss out on..."', score: 94 },
    { type: 'CTA Button', text: '"Shop Now"', score: 87 },
    { type: 'Preview Text', text: '"Exclusive offer inside..."', score: 91 },
  ];

  return (
    <div className="msg-viz-container ai-content-viz">
      <div className="msg-viz-header">
        <span className="msg-viz-label">AI CONTENT ASSISTANT</span>
        <span className="msg-viz-badge purple">AI-POWERED</span>
      </div>
      
      <div className="ai-suggestions">
        {suggestions.map((sug, i) => (
          <div key={i} className="ai-suggestion-card">
            <div className="suggestion-header">
              <span className="suggestion-type">{sug.type}</span>
              <div className="suggestion-score">
                <Sparkles size={12} />
                <span>{sug.score}%</span>
              </div>
            </div>
            <p className="suggestion-text">{sug.text}</p>
          </div>
        ))}
      </div>
      
      <div className="ai-capabilities">
        <div className="ai-cap">
          <Brain size={14} />
          <span>Generative AI</span>
        </div>
        <div className="ai-cap">
          <Zap size={14} />
          <span>Instant Generation</span>
        </div>
      </div>
    </div>
  );
};

// Campaign Builder Visualization
const CampaignBuilderVisualization = () => {
  const steps = [
    { name: 'Audience', status: 'complete' },
    { name: 'Content', status: 'complete' },
    { name: 'Schedule', status: 'active' },
    { name: 'Review', status: 'pending' },
  ];

  return (
    <div className="msg-viz-container builder-viz">
      <div className="msg-viz-header">
        <span className="msg-viz-label">CAMPAIGN BUILDER</span>
        <span className="msg-viz-badge">NO-CODE</span>
      </div>
      
      <div className="builder-steps">
        {steps.map((step, i) => (
          <div key={i} className={`builder-step ${step.status}`}>
            <div className="step-indicator">
              {step.status === 'complete' && <CheckCircle2 size={16} />}
              {step.status === 'active' && <span className="step-num">{i + 1}</span>}
              {step.status === 'pending' && <span className="step-num">{i + 1}</span>}
            </div>
            <span className="step-name">{step.name}</span>
            {i < steps.length - 1 && <div className="step-line"></div>}
          </div>
        ))}
      </div>
      
      <div className="builder-preview">
        <div className="preview-box">
          <div className="preview-header"></div>
          <div className="preview-content">
            <div className="preview-line short"></div>
            <div className="preview-line"></div>
            <div className="preview-line medium"></div>
            <div className="preview-btn"></div>
          </div>
        </div>
        <div className="preview-stats">
          <span>Est. Reach: 124K</span>
          <span>Est. Open: 32%</span>
        </div>
      </div>
      
      <div className="builder-features">
        <div className="builder-feat">
          <Workflow size={14} />
          <span>Drag & Drop</span>
        </div>
        <div className="builder-feat">
          <Layers size={14} />
          <span>Templates</span>
        </div>
        <div className="builder-feat">
          <Settings size={14} />
          <span>A/B Testing</span>
        </div>
      </div>
    </div>
  );
};

// Analytics Visualization
const AnalyticsVisualization = () => {
  const metrics = [
    { label: 'Delivered', value: '98.7%', icon: Send },
    { label: 'Open Rate', value: '34.2%', icon: Eye },
    { label: 'Click Rate', value: '12.8%', icon: MousePointer },
    { label: 'Conversion', value: '4.7%', icon: TrendingUp },
  ];

  return (
    <div className="msg-viz-container analytics-viz">
      <div className="msg-viz-header">
        <span className="msg-viz-label">REAL-TIME ANALYTICS</span>
        <span className="msg-viz-badge green">LIVE</span>
      </div>
      
      <div className="analytics-grid">
        {metrics.map((metric, i) => (
          <div key={i} className="analytics-card">
            <metric.icon size={18} className="analytics-icon" />
            <span className="analytics-value">{metric.value}</span>
            <span className="analytics-label">{metric.label}</span>
          </div>
        ))}
      </div>
      
      <div className="funnel-chart">
        <div className="funnel-stage" style={{ width: '100%' }}>
          <span>Sent: 2.4M</span>
        </div>
        <div className="funnel-stage" style={{ width: '85%' }}>
          <span>Delivered: 2.37M</span>
        </div>
        <div className="funnel-stage" style={{ width: '45%' }}>
          <span>Opened: 847K</span>
        </div>
        <div className="funnel-stage" style={{ width: '20%' }}>
          <span>Clicked: 124K</span>
        </div>
      </div>
      
      <div className="analytics-footer">
        <div className="analytics-feat">
          <BarChart3 size={14} />
          <span>Custom Reports</span>
        </div>
        <div className="analytics-feat">
          <Activity size={14} />
          <span>Live Tracking</span>
        </div>
      </div>
    </div>
  );
};

// Hero Section
const MessagingHero = () => {
  return (
    <section className="msg-hero">
      <div className="msg-hero-content">
        <div className="msg-hero-text">
          <span className="msg-label">OMNICHANNEL MESSAGING</span>
          <h1>Email, SMS & WhatsApp <span className="highlight">Unified</span></h1>
          <p>
            Reach your customers on their preferred channels with personalized 
            messaging across Email, SMS, and WhatsApp—all powered by our 
            consumer intelligence platform for maximum engagement and conversions.
          </p>
          <Link to="/contact">
            <Button className="btn-primary-zeta">
              Explore Capabilities
              <ArrowRight size={18} />
            </Button>
          </Link>
        </div>
        <div className="msg-hero-visual">
          <MessagingHeroVisualization />
        </div>
      </div>
    </section>
  );
};

// Intro Section
const MessagingIntro = () => {
  return (
    <section className="msg-intro">
      <div className="msg-intro-content">
        <span className="msg-intro-label">One Platform, Three Powerful Channels</span>
        <p>
          Email for detailed content, SMS for urgent updates, WhatsApp for conversational 
          engagement—our unified platform orchestrates all three channels with consistent 
          personalization and optimal timing based on consumer behavior signals.
        </p>
      </div>
    </section>
  );
};

// Feature Section Component
const MessagingFeatureSection = ({ 
  title, 
  description, 
  bullets, 
  visualization: Visualization,
  reversed = false
}) => {
  return (
    <section className={`msg-feature-section ${reversed ? 'reversed' : ''}`}>
      <div className="msg-feature-wrapper">
        <div className="msg-feature-visual">
          <Visualization />
        </div>
        <div className="msg-feature-text">
          <h2>{title}</h2>
          <p className="msg-feature-desc">{description}</p>
          <div className="msg-bullet-list">
            {bullets.map((bullet, index) => (
              <div key={index} className="msg-bullet-item">
                <div className="msg-bullet-bar"></div>
                <p>{bullet}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Quote Section
const MessagingQuote = () => (
  <section className="msg-quote-section">
    <div className="msg-quote-content">
      <blockquote>
        "IntentLogic's messaging campaigns consistently outperform our expectations. 
        Their team's ability to customize send-time, content, and channel selection 
        has transformed our customer engagement."
      </blockquote>
      <cite>— Enterprise Client, 2024</cite>
    </div>
  </section>
);

// FAQ Section
const MessagingFAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "What makes IntentLogic's messaging services different?",
      answer: "We combine real-time personalization, predictive content, and generative AI—all executed by our expert team. You get enterprise-grade campaigns without building internal capabilities."
    },
    {
      question: "How do you improve message relevancy for my brand?",
      answer: "We use our Identity Graph and data capabilities to detect who your customers are and what they want. We match intent to content in real time, driving higher open rates and conversions."
    },
    {
      question: "How fast can you launch campaigns?",
      answer: "Our team uses advanced workflows and AI-assisted tools to move from concept to live campaign quickly. We accelerate your time-to-market significantly compared to in-house execution."
    },
    {
      question: "What results can I expect?",
      answer: "Our clients see higher engagement, faster time-to-market, and greater ROI from their messaging channels. We turn your messaging into a revenue engine."
    }
  ];

  return (
    <section className="msg-faq-section">
      <h2>FAQs</h2>
      <div className="msg-faq-list">
        {faqs.map((faq, index) => (
          <div 
            key={index} 
            className={`msg-faq-item ${openIndex === index ? 'open' : ''}`}
          >
            <button 
              className="msg-faq-question"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <span>{faq.question}</span>
              {openIndex === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {openIndex === index && (
              <div className="msg-faq-answer">
                <p>{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

// CTA Section
const MessagingCTA = () => (
  <section className="msg-cta-section" id="msg-cta">
    <div className="msg-cta-content">
      <h2>Unify Your Messaging Across Email, SMS & WhatsApp</h2>
      <p>
        Partner with IntentLogic to deliver consistent, personalized experiences 
        across all three channels. We handle the complexity so you can focus on 
        growing your business.
      </p>
      <Link to="/contact">
        <Button className="btn-primary-zeta">
          Schedule Consultation
          <ArrowRight size={18} />
        </Button>
      </Link>
    </div>
  </section>
);

// Main Messaging Page Component
function MessagingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <SEO 
        title="Omnichannel Messaging - Email, SMS & WhatsApp Unified"
        description="Reach your customers on their preferred channels with personalized messaging across Email, SMS, and WhatsApp—all powered by consumer intelligence for maximum engagement and conversions."
        keywords="omnichannel marketing, email marketing, SMS marketing, WhatsApp business, cross-channel messaging, marketing automation, personalized messaging, customer engagement, unified communications"
        canonical="/omnichannel"
      />
      <MessagingHero />
      <MessagingIntro />
      
      <MessagingFeatureSection
        title="Email Campaigns That Convert"
        description="Rich, personalized email experiences with dynamic content, optimized send times, and AI-powered subject lines that drive opens and clicks."
        bullets={[
          "Dynamic content personalization based on consumer behavior and preferences.",
          "AI-optimized subject lines and send time optimization.",
          "Automated drip campaigns and lifecycle messaging."
        ]}
        visualization={PersonalizationVisualization}
      />
      
      <MessagingFeatureSection
        title="SMS for Instant Engagement"
        description="Time-sensitive messages delivered directly to your customers' phones with high open rates and immediate action potential."
        bullets={[
          "98% open rates with immediate delivery for urgent communications.",
          "Two-way messaging for customer service and engagement.",
          "Compliance-first approach with opt-in management and TCPA compliance."
        ]}
        visualization={AIContentVisualization}
        reversed
      />
      
      <MessagingQuote />
      
      <MessagingFeatureSection
        title="WhatsApp for Conversational Commerce"
        description="Engage customers on the world's most popular messaging app with rich media, interactive messages, and conversational experiences."
        bullets={[
          "Rich media messaging with images, videos, and interactive buttons.",
          "Automated chatbot flows for customer support and sales.",
          "Global reach with 2B+ active users across 180+ countries."
        ]}
        visualization={CampaignBuilderVisualization}
      />
      
      <MessagingFeatureSection
        title="Unified Analytics Across All Channels"
        description="Single dashboard view of performance across Email, SMS, and WhatsApp with cross-channel attribution and optimization insights."
        bullets={[
          "Real-time analytics across all three messaging channels.",
          "Cross-channel customer journey tracking and attribution.",
          "AI-powered recommendations for channel and timing optimization."
        ]}
        visualization={AnalyticsVisualization}
        reversed
      />
      
      <MessagingFAQ />
      <MessagingCTA />
    </Layout>
  );
}

export default MessagingPage;
